import React, { useMemo, useState } from "react";
import {
    Box,
    Typography,
    Stack,
    TextField,
    InputAdornment,
    MenuItem,
    Select,
    FormControl,
    Chip,
    CircularProgress,
    useTheme,
    useMediaQuery,
} from "@mui/material";

import SearchIcon from "@mui/icons-material/Search";

import LeadDetailsDialog from "../components/leads/LeadDetailsDialog";
import LeadsTable from "../components/leads/LeadsTable";
import LeadsCards from "../components/leads/LeadsCards";

import { useLeadsList } from "../hooks/useLeadsList";

export default function LeadsPage() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));

    const [q, setQ] = useState("");
    const [status, setStatus] = useState("Todos");
    const [owner, setOwner] = useState("Todos");

    const [selectedLead, setSelectedLead] = useState(null);
    const [openDetails, setOpenDetails] = useState(false);

    const { leads, loading } = useLeadsList();

    const openLeadDetails = (lead) => {
        setSelectedLead(lead);
        setOpenDetails(true);
    };

    const owners = useMemo(
        () => ["Todos", ...Array.from(new Set(leads.map((l) => l.owner).filter(Boolean)))],
        [leads]
    );

    const statuses = ["Todos", "Sem resposta", "Em conversa", "Fechado"];

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();

        return leads.filter((l) => {
            const matchText =
                !term ||
                l.name.toLowerCase().includes(term) ||
                l.phone.toLowerCase().includes(term) ||
                l.stage.toLowerCase().includes(term);

            const matchStatus = status === "Todos" ? true : l.status === status;
            const matchOwner = owner === "Todos" ? true : l.owner === owner;

            return matchText && matchStatus && matchOwner;
        });
    }, [q, status, owner, leads]);

    return (
        <Box p={2}>
            {loading && (
                <Box display="flex" justifyContent="center" py={6}>
                    <CircularProgress size={32} />
                </Box>
            )}
            {/* HEADER */}
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-end"
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 900,
                            color: "var(--cise-blue)",
                            letterSpacing: -0.5,
                        }}
                    >
                        Leads
                    </Typography>

                    <Typography sx={{ color: "var(--cise-text-muted)" }}>
                        Gerencie e priorize atendimentos do SDR
                    </Typography>
                </Box>

                <Chip
                    label={`${filtered.length} leads`}
                    sx={{
                        border: "1px solid var(--border)",
                        bgcolor: "var(--branco)",
                        fontWeight: 800,
                    }}
                />
            </Stack>

            {/* FILTROS */}
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 1,
                    border: "1px solid var(--border)",
                    bgcolor: "var(--branco)",
                }}
            >
                <TextField
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Buscar por nome, telefone, etapa..."
                    size="small"
                    fullWidth
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        "& .MuiOutlinedInput-root": {
                            borderRadius: 999,
                            background: "var(--bg)",
                        },
                    }}
                />

                <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 220 } }}>
                    <Select
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        sx={{ borderRadius: 999, background: "var(--bg)" }}
                    >
                        {statuses.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 220 } }}>
                    <Select
                        value={owner}
                        onChange={(e) => setOwner(e.target.value)}
                        sx={{ borderRadius: 999, background: "var(--bg)" }}
                    >
                        {owners.map((o) => (
                            <MenuItem key={o} value={o}>
                                {o}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Stack>

            {/* CONTEÚDO */}
            {isMobile ? (
                <LeadsCards leads={filtered} onOpenLead={openLeadDetails} />
            ) : (
                <LeadsTable leads={filtered} onOpenLead={openLeadDetails} />
            )}

            <LeadDetailsDialog
                open={openDetails}
                lead={selectedLead}
                onClose={() => setOpenDetails(false)}
            />
        </Box>

    );
}